import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { VictoryLabel, VictoryPie } from 'victory';
import dayjs from 'dayjs';
import { ContractDistributionDashboardDto } from 'api/dashboard/dtos/ContractDistributionDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import useElementSize from 'common/hooks/useElementSize';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import Utils from 'common/services/Utils';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import ChartTooltipPortal from '../components/ChartTooltipPortal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './ContractsDistribution.module.scss';

const CONTRACT_COLORS: Record<string, string> = {
	USE: '#02c7c7',
	INSURANCE: '#038cb6',
	MAINTENANCE: '#7c5cbf',
};

const CONTRACTS_FILTERS_CACHE_KEY = 'contracts-listing-filters';

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const ContractsDistribution = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<ContractDistributionDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [hiddenTypes, setHiddenTypes] = useState<string[]>([]);
	const [containerRef, { width, height }] = useElementSize();
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('contractsDistribution');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());
	const [tooltip, setTooltip] = useState<{ name: string; value: number; color: string; clientX: number; clientY: number } | null>(null);

	const effectiveFrom = props.filters?.dateFrom ?? dateFrom;
	const effectiveTo = props.filters?.dateTo ?? dateTo;
	const datesRef = useRef({ dateFrom: effectiveFrom, dateTo: effectiveTo });
	datesRef.current = { dateFrom: effectiveFrom, dateTo: effectiveTo };

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getContractsDistribution(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const isHorizontal = width > height * 1.3;
	const legendWidth = isHorizontal ? Math.min(width * 0.38, 160) : 0;
	const donutAreaWidth = isHorizontal ? width - legendWidth : width;
	const donutAreaHeight = isHorizontal ? height : Math.max(height - 30, 150);
	const donutSize = Math.min(donutAreaWidth, donutAreaHeight);
	const pieRadius = donutSize * 0.44;
	const pieInnerRadius = pieRadius * 0.7467;

	const allItems = (data || []).filter(x => x.totalCost > 0);
	const itemsData = allItems.filter(x => !hiddenTypes.includes(x.contractType));
	const colorScale = itemsData.map(x => CONTRACT_COLORS[x.contractType] || '#999');
	const visibleTotal = itemsData.reduce((a, b) => a + b.totalCost, 0);
	const grandTotal = allItems.reduce((a, b) => a + b.totalCost, 0);

	const emptyState = !isLoading && allItems.length === 0;

	const toggleType = (type: string) => {
		setHiddenTypes(prev => prev.includes(type) ? prev.filter(x => x !== type) : [...prev, type]);
	};

	const navigateToContracts = (contractType: string) => {
		const typeLabelKey = `contracts_listing.type_${contractType.toLowerCase()}`;
		const { dateFrom: fromNow, dateTo: toNow } = datesRef.current;
		const normalizedFrom = dayjs(fromNow).startOf('month').toDate();
		const normalizedTo = dayjs(toNow).endOf('month').toDate();
		// showOnlyLast esconde contratos sobrepostos (por veículo+tipo mantém só o mais
		// recente) e faz a listagem divergir do total do widget — o widget soma TODOS
		// os contratos activos no período.
		const filters = {
			contractType,
			contractTypeName: t(typeLabelKey as any) as string,
			contractDateFrom: normalizedFrom,
			contractDateTo: normalizedTo,
			showOnlyLast: false,
			// vehiclesInactive: false — dashboard agrega só viaturas ativas; clique mantém o critério.
			vehiclesInactive: false,
		};
		try {
			localStorage.setItem(CONTRACTS_FILTERS_CACHE_KEY, JSON.stringify(filters));
		} catch { /* silent */ }
		window.open('/contracts', '_blank');
	};

	// eslint-disable-next-line
	const pieEvents: any[] = [
		{
			target: 'data',
			eventHandlers: {
				onMouseEnter: (evt: any, targetProps: any) => {
					const datum = targetProps.datum;
					const name = t(`dashboard.contract_type_${(datum.contractType as string).toLowerCase()}` as any) as string;
					const color = CONTRACT_COLORS[datum.contractType] || '#999';
					setTooltip({
						name,
						value: datum.totalCost,
						color,
						clientX: evt.clientX,
						clientY: evt.clientY,
					});
					return [];
				},
				onMouseMove: (evt: any) => {
					setTooltip(prev => prev ? { ...prev, clientX: evt.clientX, clientY: evt.clientY } : null);
					return [];
				},
				onMouseLeave: () => {
					setTooltip(null);
					return [];
				},
				onClick: (_evt: any, targetProps: any) => {
					const datum = targetProps.datum;
					if (datum?.contractType) navigateToContracts(datum.contractType);
					return [];
				},
			},
		},
	];

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.contracts_distribution') as string}
			subTitleTooltip={t('dashboard.contracts_distribution_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<div className={styles.chartContainer} ref={containerRef}>
				{isLoading ? (
					<div className={styles.centerAlign}><LoadingSpinner /></div>
				) : emptyState ? (
					<WidgetNoData />
				) : (
					width > 0 && height > 0 && (
						<div className={isHorizontal ? styles.layoutHorizontal : styles.layoutVertical}>
							<div className={styles.donutWrapper} style={{ width: donutAreaWidth, height: donutAreaHeight }}>
								<svg width={donutAreaWidth} height={donutAreaHeight}>
									<VictoryPie
										standalone={false}
										colorScale={colorScale}
										innerRadius={pieInnerRadius}
										radius={pieRadius}
										width={donutAreaWidth}
										height={donutAreaHeight}
										data={itemsData}
										x="contractType"
										y="totalCost"
										events={pieEvents}
										style={{ data: { cursor: 'pointer', stroke: '#fff', strokeWidth: 1 } }}
										labelComponent={<VictoryLabel text="" />}
									/>
								</svg>
								{(() => {
									// € colado ao número + auto-shrink para não quebrar em
									// duas linhas em valores grandes tipo "1 226 277,54€".
									const totalText = Utils.formatCurrency(visibleTotal).replace(' €', '€');
									const innerWidth = pieInnerRadius * 1.7;
									const baseFontSize = pieInnerRadius * 0.28;
									const approxCharWidth = baseFontSize * 0.55;
									const textWidth = totalText.length * approxCharWidth;
									const fitFactor = textWidth > innerWidth ? innerWidth / textWidth : 1;
									const fontSize = Math.max(baseFontSize * fitFactor, 9);
									return (
										<div className={styles.centerLabel} style={{ maxWidth: innerWidth }}>
											<div className={styles.centerLabelSub}>{t('dashboard.total' as any)}</div>
											<div className={styles.centerLabelValue} style={{ fontSize }}>{totalText}</div>
										</div>
									);
								})()}
							</div>
							<div className={isHorizontal ? styles.htmlLegendRight : styles.htmlLegend}>
								{allItems.map((item, i) => {
									const hidden = hiddenTypes.includes(item.contractType);
									const pct = grandTotal > 0 ? Math.round((item.totalCost / grandTotal) * 100) : 0;
									return (
										<span
											key={i}
											className={`${styles.legendItem} ${hidden ? styles.legendItemHidden : ''}`}
											onClick={() => toggleType(item.contractType)}
										>
											<span className={styles.legendDot} style={{ backgroundColor: hidden ? '#d1d5db' : (CONTRACT_COLORS[item.contractType] || '#999') }} />
											<span className={styles.legendName}>{t(`dashboard.contract_type_${item.contractType.toLowerCase()}` as any)}</span>
											<span className={styles.legendPct}>{pct}%</span>
										</span>
									);
								})}
							</div>
						</div>
					)
				)}

				{tooltip && (
					<ChartTooltipPortal clientX={tooltip.clientX} clientY={tooltip.clientY} className={styles.tooltip}>
						<div className={styles.tooltipRow}>
							<span className={styles.tooltipDot} style={{ backgroundColor: tooltip.color }} />
							<span>{tooltip.name}</span>
						</div>
						<div className={styles.tooltipValue}>
							{Utils.formatCurrency(tooltip.value)}
						</div>
					</ChartTooltipPortal>
				)}
			</div>
		</Box>
	);
};

export default ContractsDistribution;
